import { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

interface UserDeleteZoneProps {
    userName: string;
    isDeleting: boolean;
    onConfirmDelete: () => void;
    /** Opens straight into the confirm step - used when the row menu's
     *  "Delete user" action opens the drawer, so the user doesn't have to
     *  click Delete twice. */
    initialConfirming?: boolean;
}

// Inline two-step confirm (not a separate modal) per the design source's
// UserDrawer danger zone - clicking Delete swaps the card into a confirm
// state within the same panel rather than layering another overlay.
export function UserDeleteZone({
    userName,
    isDeleting,
    onConfirmDelete,
    initialConfirming = false
}: UserDeleteZoneProps) {
    const [confirming, setConfirming] = useState(initialConfirming);

    return (
        <div
            className={`rounded-xl border ${
                confirming ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
            }`}
        >
            <div className="px-4 py-3.5 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <span className="block text-[13px] font-bold text-gray-900">Delete user</span>
                    <span className="block text-[12px] text-gray-500 leading-snug mt-0.5">
                        Deletes the account and revokes all roles, permissions, and practice access.
                        Re-adding them later with the same email reuses their account, but roles and
                        permissions must be granted again.
                    </span>
                </div>
                {!confirming && (
                    <button
                        type="button"
                        onClick={() => setConfirming(true)}
                        className="shrink-0 h-9 px-3.5 rounded-lg border border-red-200 bg-white text-[13px] font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                        <TrashIcon className="h-3.5 w-3.5" />
                        Delete
                    </button>
                )}
            </div>
            {confirming && (
                <div className="px-4 pb-3.5 flex items-center gap-2.5">
                    <span className="text-[12px] font-semibold text-red-800 flex-1">
                        Delete {userName}? They&apos;ll lose access immediately, and re-adding them
                        later won&apos;t restore their roles or permissions.
                    </span>
                    <button
                        type="button"
                        disabled={isDeleting}
                        onClick={() => setConfirming(false)}
                        className="h-9 px-3 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-white disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={isDeleting}
                        onClick={onConfirmDelete}
                        className="h-9 px-3.5 rounded-lg bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700 disabled:opacity-50"
                    >
                        {isDeleting ? 'Deleting…' : 'Yes, delete'}
                    </button>
                </div>
            )}
        </div>
    );
}
